// Utility type to filter properties of a given type
import type { RouteName, RouteParams } from 'ziggy-js'

export type QueryParams<T> = {
  [K in keyof T]?: T[K] extends string | number | boolean ? T[K] : never
}

/**
 * Parse URL query parameters from a URL string
 *
 * Handles both indexed array format (key[0]=value1&key[1]=value2) and
 * non-indexed array format (key[]=value1&key[]=value2), normalizing both
 * to the base key with an array value (key: ['value1', 'value2']).
 *
 * @param url The full URL string (e.g., 'https://example.com/path?foo=bar&baz=qux')
 * @returns An object containing the parsed query parameters
 *
 * @example
 * ```ts
 * const params = parseUrlParams('https://example.com/path?foo=bar&baz=qux')
 * // { foo: 'bar', baz: 'qux' }
 *
 * const arrayParams = parseUrlParams('https://example.com/path?ids[]=1&ids[]=2')
 * // { ids: ['1', '2'] }
 *
 * const indexedArrayParams = parseUrlParams('https://example.com/path?ids[0]=1&ids[1]=2')
 * // { ids: ['1', '2'] }
 * ```
 */
export const parseUrlParams = (url: string): Record<string, string | string[]> => {
  const urlSearchParams = new URLSearchParams(url.split('?')[1] || '')
  const params: Record<string, string | string[]> = {}
  const arrayParams: Record<string, string[]> = {}

  for (const key of urlSearchParams.keys()) {
    const values = urlSearchParams.getAll(key)

    // Check if this is an indexed array parameter (e.g., 'payment_status[0]', 'payment_status[1]')
    const indexedArrayMatch = key.match(/^(.+)\[\d+\]$/)

    if (indexedArrayMatch) {
      // Extract base key (e.g., 'payment_status' from 'payment_status[0]')
      const baseKey = indexedArrayMatch[1]

      // Initialize array if not exists
      if (!arrayParams[baseKey]) {
        arrayParams[baseKey] = []
      }

      // Add all values for this indexed key
      arrayParams[baseKey].push(...values)
    } else {
      // Regular parameter or non-indexed array format (key[])
      const isNonIndexedArray = key.endsWith('[]')
      const normalizedKey = isNonIndexedArray ? key.slice(0, -2) : key

      if (values.length > 1 || isNonIndexedArray) {
        // Multiple values or explicit array notation - store as array
        params[normalizedKey] = values
      } else {
        // Single value - store as string
        params[normalizedKey] = values[0]
      }
    }
  }

  // Merge indexed array params into main params object
  Object.entries(arrayParams).forEach(([key, values]) => {
    params[key] = values
  })

  return params
}

// TQuery must extend Record<string, any>, not Record<string, unknown>: this constraint is
// checked against every concrete query-params type across the app, most of which are plain
// interfaces without an index signature — `Record<string, unknown>` rejects those with
// "index signature is missing" errors, while `any` satisfies the constraint unconditionally.
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- see comment above
export const buildQueryParams = <TQuery extends Record<string, any>>(params: TQuery): string => {
  const searchParams = new URLSearchParams()

  for (const [key, value] of Object.entries(params)) {
    // Skip null or undefined values so they don't appear in the query string
    if (value === null || value === undefined) {
      continue
    }

    // Check if the value is an array
    if (Array.isArray(value)) {
      // If it is an array, loop through it and append each item
      // with the square bracket notation on the key.
      value.forEach(item => {
        searchParams.append(`${key}[]`, String(item))
      })
    } else {
      // If it's not an array, append it as a single value.
      searchParams.append(key, String(value))
    }
  }
  return searchParams.toString()
}

export const buildUrl = <TQuery>(baseUrl: string, endpoint: string, queryParams?: QueryParams<TQuery>) => {
  const url = new URL(endpoint, baseUrl)
  if (queryParams) {
    url.search = buildQueryParams(queryParams)
  }
  return url.toString()
}

const UNKNOWN_REQUEST_PATH = 'an unknown address'

/**
 * Reduce a request URL to the path a person can recognise, dropping the origin and the
 * query string. Two requests to the same endpoint then share one path, which is what
 * lets the server-error reporter treat them as one problem.
 *
 * @example
 * ```ts
 * toRequestPath('https://acme.twine.test/admin/products?page=2') // '/admin/products'
 * toRequestPath('/admin/products')                               // '/admin/products'
 * toRequestPath(undefined)                                       // 'an unknown address'
 * ```
 */
export const toRequestPath = (url?: string): string => {
  if (!url) {
    return UNKNOWN_REQUEST_PATH
  }

  try {
    return new URL(url, window.location.origin).pathname
  } catch {
    return UNKNOWN_REQUEST_PATH
  }
}

export const relativeZiggyRoute = (routeName: RouteName, params?: RouteParams<RouteName> | undefined) => {
  // To trim a leading slash (/) from route
  try {
    return route(routeName, params, false).replace(/^\/+/, '')
  } catch {
    return 'unknown-route'
  }
}
